import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Document, Page } from 'react-pdf';
import { triggerDownload, triggerForceDownload, useTouchPreviewDevice, buildSafeDownloadFilename } from './fileVisuals';
import '../../super-picker-public/components/detail/_pdf/pdfWorker';
import {
  fetchPreviewBlob,
  isDownloadEdmUrl,
  parseEdmIdFromDownloadEdmUrl,
  resolveEdmWithAgentPreviewUrl,
} from '../services/api';

// ---------------------------------------------------------------------------
// FilePreviewDialog
//
// Shared preview dialog used by every "download" button on the
// Project Essentials surface that points at an EDM-style asset
// (Quarterly Reports, EDMs in the catalogue / Commercial / Upcoming
// pages, plus the Highlights & eBook → Latest Highlights rail).
//
// Behaviour:
//   • Renders the file inline when it's a PDF (iframe on desktop,
//     react-pdf on touch phones/tablets — Safari shows an "Open"
//     placeholder for PDFs inside iframes), an image (<img>), or a
//     direct video file (<video>).
//   • With-agent EDM previews resolve `/public/GetEDM/<id>` to the
//     portal temp asset URL (PDF or image) and render it directly.
//     Other sources still use the proxy + blob pipeline.
//   • Preview always shows the with-agent composited version when
//     `url` is a DownloadEDM link. The public (no-agent) variant is
//     download-only — it is never previewed inline.
//   • If a `urlPublic` is supplied, a "Download with agent details"
//     checkbox switches the download target only; preview is unchanged.
//   • Footer Download button triggers the actual download against the
//     portal DownloadEDM / public URL (not the temp preview URL).
// ---------------------------------------------------------------------------

export interface FilePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Title shown in the dialog header. */
  title: string;
  /**
   * URL with agent details (default selection). `'#'` or empty is
   * treated as "no preview available" — the dialog still opens, but
   * the preview area shows a placeholder and Download is disabled.
   */
  url: string | null;
  /**
   * URL without agent details. When provided and non-empty the dialog
   * renders a download-only toggle (preview always uses `url`). When
   * `null`/omitted the toggle is hidden.
   */
  urlPublic?: string | null;
  /** Suggested save-as filename for the Download button. */
  filename: string;
  onDownloadClick?: () => void;
  /**
   * Fetch as a blob before downloading so cross-origin CDN URLs save
   * to disk instead of opening in a new tab.
   */
  forceBlobDownload?: boolean;
}

type PreviewKind = 'pdf' | 'image' | 'video' | 'unsupported';

const VIDEO_EXT_PATTERN =
  /\.(mp4|webm|mov|m4v|ogg|ogv)(?:[?#]|$)/i;
const VIDEO_FILENAME_PARAM_PATTERN =
  /filename=[^&]*\.(mp4|webm|mov|m4v|ogg|ogv)/i;

// Mobile dialog occupies ~75% of the viewport (within the user's 70–80% range).
const MOBILE_DIALOG_WIDTH = '75%';
const MOBILE_DIALOG_HEIGHT = '75dvh';

const ZOOM_MIN = 1;
const ZOOM_MAX = 2.5;

function clampZoom(scale: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
}

function roundZoom(scale: number): number {
  return Math.round(clampZoom(scale) * 100) / 100;
}

function touchDistance(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

type PinchZoomVisual = {
  contentRef: React.RefObject<HTMLElement | null>;
  spacerRef: React.RefObject<HTMLElement | null>;
  /** Unscaled content size (transform does not affect layout). */
  getBaseSize: () => { width: number; height: number };
  minWidth?: number;
  labelRef?: React.RefObject<HTMLElement | null>;
};

/** Attaches non-passive pinch listeners so CSS zoom can update without page zoom.
 * During the gesture, scale/scroll are applied imperatively (no React re-render per move)
 * so the zoom stays smooth; React state is committed when the pinch ends.
 */
function usePinchZoomOnElement(
  elementRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  onZoomScaleChange?: (scale: number) => void,
  zoomScale = ZOOM_MIN,
  visual?: PinchZoomVisual,
) {
  const pinchRef = useRef<{
    startDist: number;
    startScale: number;
    originContentX: number;
    originContentY: number;
  } | null>(null);
  const liveScaleRef = useRef(zoomScale);
  const zoomScaleRef = useRef(zoomScale);
  const onZoomScaleChangeRef = useRef(onZoomScaleChange);
  const visualRef = useRef(visual);
  const rafRef = useRef(0);
  const pendingMoveRef = useRef<{
    scale: number;
    midX: number;
    midY: number;
  } | null>(null);
  const pendingFocalRef = useRef<{
    contentX: number;
    contentY: number;
    midX: number;
    midY: number;
  } | null>(null);

  useEffect(() => {
    zoomScaleRef.current = zoomScale;
    liveScaleRef.current = zoomScale;
  }, [zoomScale]);

  useEffect(() => {
    onZoomScaleChangeRef.current = onZoomScaleChange;
  }, [onZoomScaleChange]);

  useEffect(() => {
    visualRef.current = visual;
  }, [visual]);

  // After React commits the final scale, drop inline overrides and re-anchor scroll.
  useLayoutEffect(() => {
    const el = elementRef.current;
    const v = visualRef.current;
    const content = v?.contentRef.current;
    const spacer = v?.spacerRef.current;
    if (content) content.style.transform = '';
    if (spacer) {
      spacer.style.width = '';
      spacer.style.height = '';
      spacer.style.minHeight = '';
    }
    const pending = pendingFocalRef.current;
    if (!el || !pending) return;
    el.scrollLeft = pending.contentX * zoomScale - pending.midX;
    el.scrollTop = pending.contentY * zoomScale - pending.midY;
    pendingFocalRef.current = null;
  }, [elementRef, zoomScale]);

  useEffect(() => {
    const el = elementRef.current;
    if (!el || !enabled) return;

    const applyVisual = (scale: number, midX: number, midY: number) => {
      const pinch = pinchRef.current;
      if (!pinch) return;
      const v = visualRef.current;
      const content = v?.contentRef.current;
      const spacer = v?.spacerRef.current;
      const label = v?.labelRef?.current;
      if (content) {
        content.style.transform = `scale(${scale})`;
      }
      if (spacer && v) {
        const { width, height } = v.getBaseSize();
        const w = Math.max(width * scale, v.minWidth ?? 0);
        const h = Math.max(height * scale, 0);
        if (w > 0) spacer.style.width = `${w}px`;
        if (h > 0) {
          spacer.style.height = `${h}px`;
          spacer.style.minHeight = `${h}px`;
        }
      }
      if (label) {
        label.textContent = `${Math.round(scale * 100)}%`;
      }
      el.scrollLeft = pinch.originContentX * scale - midX;
      el.scrollTop = pinch.originContentY * scale - midY;
      liveScaleRef.current = scale;
      pendingFocalRef.current = {
        contentX: pinch.originContentX,
        contentY: pinch.originContentY,
        midX,
        midY,
      };
    };

    const flushPending = () => {
      rafRef.current = 0;
      const pending = pendingMoveRef.current;
      if (!pending || !pinchRef.current) return;
      applyVisual(pending.scale, pending.midX, pending.midY);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) {
        pinchRef.current = null;
        return;
      }
      const scale = liveScaleRef.current || ZOOM_MIN;
      const rect = el.getBoundingClientRect();
      const midX =
        (event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left;
      const midY =
        (event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top;
      pinchRef.current = {
        startDist: touchDistance(event.touches[0], event.touches[1]),
        startScale: scale,
        originContentX: (el.scrollLeft + midX) / scale,
        originContentY: (el.scrollTop + midY) / scale,
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!pinchRef.current || event.touches.length !== 2) return;
      event.preventDefault();
      const dist = touchDistance(event.touches[0], event.touches[1]);
      if (pinchRef.current.startDist <= 0) return;
      const next = clampZoom(
        pinchRef.current.startScale * (dist / pinchRef.current.startDist),
      );
      const rect = el.getBoundingClientRect();
      const midX =
        (event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left;
      const midY =
        (event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top;
      pendingMoveRef.current = { scale: next, midX, midY };
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(flushPending);
      }
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length >= 2) return;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
        flushPending();
      }
      if (!pinchRef.current) return;
      pinchRef.current = null;
      pendingMoveRef.current = null;
      const committed = roundZoom(liveScaleRef.current);
      liveScaleRef.current = committed;
      zoomScaleRef.current = committed;
      // Keep inline transform until layout settles; React may skip render if
      // the rounded scale is unchanged, so also clear on the next frame.
      onZoomScaleChangeRef.current?.(committed);
      requestAnimationFrame(() => {
        const v = visualRef.current;
        const content = v?.contentRef.current;
        const spacer = v?.spacerRef.current;
        if (content) content.style.transform = '';
        if (spacer) {
          spacer.style.width = '';
          spacer.style.height = '';
          spacer.style.minHeight = '';
        }
        const pending = pendingFocalRef.current;
        if (pending) {
          el.scrollLeft = pending.contentX * committed - pending.midX;
          el.scrollTop = pending.contentY * committed - pending.midY;
          pendingFocalRef.current = null;
        }
      });
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [elementRef, enabled]);
}

function PinchZoomSurface({
  enabled,
  zoomScale,
  onZoomScaleChange,
  zoomLabelRef,
  sx,
  children,
}: {
  enabled: boolean;
  zoomScale: number;
  onZoomScaleChange?: (scale: number) => void;
  zoomLabelRef?: React.RefObject<HTMLElement | null>;
  sx?: object;
  children: React.ReactNode;
}) {
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const contentSizeRef = useRef({ width: 0, height: 0 });
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });

  const visual = useMemo<PinchZoomVisual>(
    () => ({
      contentRef,
      spacerRef,
      getBaseSize: () => contentSizeRef.current,
      labelRef: zoomLabelRef,
    }),
    [zoomLabelRef],
  );

  usePinchZoomOnElement(surfaceRef, enabled, onZoomScaleChange, zoomScale, visual);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => {
      const next = {
        width: el.offsetWidth || 0,
        height: el.offsetHeight || 0,
      };
      contentSizeRef.current = next;
      setContentSize(next);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children]);

  const scaledWidth = contentSize.width * zoomScale;
  const scaledHeight = contentSize.height * zoomScale;

  return (
    <Box
      ref={surfaceRef}
      sx={{
        touchAction: 'pan-x pan-y',
        WebkitOverflowScrolling: 'touch',
        ...sx,
      }}
    >
      {/* Layout spacer matches visual scaled size so overflow can scroll to the pinch focus. */}
      <Box
        ref={spacerRef}
        sx={{
          position: 'relative',
          width: contentSize.width > 0 ? scaledWidth : '100%',
          height: contentSize.height > 0 ? scaledHeight : '100%',
          minHeight: contentSize.height > 0 ? scaledHeight : undefined,
          mx: 'auto',
          flexShrink: 0,
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            maxWidth: contentSize.width > 0 ? undefined : '100%',
            transform: `scale(${zoomScale})`,
            transformOrigin: 'top left',
            willChange: enabled ? 'transform' : undefined,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

// Map an upstream MIME type onto our render branches. The portal's
// "force download" endpoints often respond with a generic type
// (`application/octet-stream`, `application/force-download`, missing
// header, etc.), in which case we get no signal here and fall back
// to URL/filename hints in `resolvePreviewKind` below.
function kindFromContentType(contentType: string): PreviewKind {
  const ct = (contentType || '').toLowerCase();
  if (ct.includes('pdf')) return 'pdf';
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('video/')) return 'video';
  return 'unsupported';
}

// URL-based classifier. Looks at:
//   1. `fileName` query param (the portal's `Home/Download` URL
//      stamps this; it reflects the source file type and is the
//      canonical hint for both with-agent and public variants).
//   2. The pathname's trailing extension.
//   3. Endpoint-shape hint: `/public/DownloadTemplate/` is the
//      portal's PDF-only public endpoint in our wiring, so we treat
//      it as a strong PDF signal even when the URL has no
//      extension.
// Deliberately does NOT special-case `/public/DownloadEDM/` — that
// with-agent endpoint serves whatever the source actually is (PDF
// or image).
function kindFromUrl(rawUrl: string | null | undefined): PreviewKind {
  if (!rawUrl || rawUrl === '#') return 'unsupported';
  const lower = rawUrl.toLowerCase();
  if (/\.(pdf)(?:[?#]|$)/.test(lower) || /filename=[^&]*\.pdf/.test(lower)) {
    return 'pdf';
  }
  if (
    /\.(jpe?g|png|gif|webp|bmp|svg)(?:[?#]|$)/.test(lower) ||
    /filename=[^&]*\.(jpe?g|png|gif|webp|bmp|svg)/.test(lower)
  ) {
    return 'image';
  }
  if (VIDEO_EXT_PATTERN.test(lower) || VIDEO_FILENAME_PARAM_PATTERN.test(lower)) {
    return 'video';
  }
  if (lower.includes('/public/downloadtemplate/')) return 'pdf';
  return 'unsupported';
}

function kindFromFilename(name: string): PreviewKind {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (/^(jpe?g|png|gif|webp|bmp|svg)$/.test(ext)) return 'image';
  if (/^(mp4|webm|mov|m4v|ogg|ogv)$/.test(ext)) return 'video';
  return 'unsupported';
}

// Combine upstream MIME and URL hints, preferring a definite hit
// from either source over `unsupported`. The public URL is the most
// reliable hint when upstream is generic — its `fileName` query
// param reflects the source file type and applies equally to the
// with-agent variant (which composites onto the same source).
function resolvePreviewKind(
  contentType: string,
  activeUrl: string | null | undefined,
  publicUrl: string | null | undefined,
  fileFilename: string,
): PreviewKind {
  const fromCt = kindFromContentType(contentType);
  if (fromCt !== 'unsupported') return fromCt;
  const fromPublic = kindFromUrl(publicUrl);
  if (fromPublic !== 'unsupported') return fromPublic;
  const fromActive = kindFromUrl(activeUrl);
  if (fromActive !== 'unsupported') return fromActive;
  return kindFromFilename(fileFilename);
}

// Suggested filename derived from the URL itself when the parent's
// preferred name is empty (e.g. no title/filename was passed).
function deriveFilename(rawUrl: string, fallback: string): string {
  try {
    const u = new URL(rawUrl);
    const fileNameParam = u.searchParams.get('fileName');
    if (fileNameParam && fileNameParam.trim()) {
      return fileNameParam.trim();
    }
    const last = u.pathname.split('/').filter(Boolean).pop();
    if (last && /\.[a-z0-9]{1,8}$/i.test(last)) {
      try {
        return decodeURIComponent(last);
      } catch {
        return last;
      }
    }
  } catch {
    // Not an absolute URL — fall through to the parent's filename.
  }
  return fallback;
}

function extensionFromUrlOrName(rawUrl: string | null | undefined, name: string): string | null {
  const fromName = name.match(/\.([a-z0-9]{1,8})$/i);
  if (fromName) return fromName[1];
  if (!rawUrl) return null;
  try {
    const u = new URL(rawUrl);
    const fileNameParam = u.searchParams.get('fileName');
    if (fileNameParam) {
      const m = fileNameParam.match(/\.([a-z0-9]{1,8})$/i);
      if (m) return m[1];
    }
    const last = u.pathname.split('/').filter(Boolean).pop() || '';
    const m = last.match(/\.([a-z0-9]{1,8})$/i);
    if (m) return m[1];
  } catch {
    // ignore
  }
  return null;
}

// iOS/iPadOS Safari does not render cross-origin PDFs inline inside
// <iframe> — it shows a document icon and an "Open" button instead.
// react-pdf fetches the bytes and rasterises pages so preview works on
// touch devices. Pages load progressively to limit memory use.
function TouchPdfScrollPreview({
  pdfUrl,
  pageWidth,
  zoomScale = 1,
  onZoomScaleChange,
  pinchZoom = false,
  zoomLabelRef,
}: {
  pdfUrl: string;
  pageWidth: number;
  zoomScale?: number;
  onZoomScaleChange?: (scale: number) => void;
  pinchZoom?: boolean;
  zoomLabelRef?: React.RefObject<HTMLElement | null>;
}) {
  const [numPages, setNumPages] = useState(0);
  const [renderedPages, setRenderedPages] = useState(1);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const contentSizeRef = useRef({ width: pageWidth, height: 0 });
  const [contentSize, setContentSize] = useState({ width: pageWidth, height: 0 });

  const visual = useMemo<PinchZoomVisual>(
    () => ({
      contentRef,
      spacerRef,
      getBaseSize: () => contentSizeRef.current,
      minWidth: pageWidth,
      labelRef: zoomLabelRef,
    }),
    [pageWidth, zoomLabelRef],
  );

  usePinchZoomOnElement(scrollRef, pinchZoom, onZoomScaleChange, zoomScale, visual);

  useEffect(() => {
    setNumPages(0);
    setRenderedPages(1);
  }, [pdfUrl]);

  useEffect(() => {
    if (numPages <= renderedPages) return;
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setRenderedPages((prev) => Math.min(prev + 2, numPages));
        }
      },
      { root, rootMargin: '240px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [numPages, renderedPages]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => {
      const next = {
        width: el.offsetWidth || pageWidth,
        height: el.offsetHeight || 0,
      };
      contentSizeRef.current = next;
      setContentSize(next);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [pdfUrl, pageWidth, renderedPages, numPages]);

  const pagesToRender = Math.min(renderedPages, numPages);
  const scaledWidth = Math.max(contentSize.width * zoomScale, pageWidth);
  const scaledHeight = Math.max(contentSize.height * zoomScale, contentSize.height);

  return (
    <Box
      ref={scrollRef}
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        bgcolor: '#fff',
        py: 1,
        // Allow drag-pan on both axes when zoomed (pan-y alone blocks horizontal).
        touchAction: 'pan-x pan-y',
        WebkitOverflowScrolling: 'touch',
        '& .react-pdf__Document': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        },
        '& .react-pdf__Page': {
          background: 'transparent !important',
        },
        '& canvas': {
          display: 'block',
          maxWidth: 'none',
          height: 'auto !important',
        },
      }}
    >
      {/* Layout spacer matches the *visual* scaled size so overflow scrolls. */}
      <Box
        ref={spacerRef}
        sx={{
          position: 'relative',
          width: scaledWidth,
          height: scaledHeight || undefined,
          minHeight: scaledHeight || undefined,
          mx: 'auto',
          flexShrink: 0,
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `scale(${zoomScale})`,
            transformOrigin: 'top left',
            willChange: pinchZoom ? 'transform' : undefined,
          }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages: total }) => {
              setNumPages(total);
              setRenderedPages(Math.min(2, total));
            }}
            loading={
              <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </Stack>
            }
            error={
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', p: 3, textAlign: 'center' }}>
                Could not render PDF preview on this device. Use Download to open the file.
              </Typography>
            }
          >
            {numPages > 0 &&
              Array.from({ length: pagesToRender }, (_, index) => (
                <Page
                  key={`page-${index + 1}`}
                  pageNumber={index + 1}
                  width={pageWidth}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  canvasBackground="#fff"
                />
              ))}
          </Document>
          {numPages > pagesToRender && (
            <Stack ref={sentinelRef} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={22} />
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function FilePreviewDialog({
  open,
  onClose,
  title,
  url,
  urlPublic,
  filename,
  onDownloadClick,
  // Default on so PDF/EDM/eBook downloads save in-tab instead of opening
  // a new browser tab when the CDN ignores Content-Disposition.
  forceBlobDownload = true,
}: FilePreviewDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchPreview = useTouchPreviewDevice();
  const [pageWidth, setPageWidth] = useState(320);
  const [mobileFullscreen, setMobileFullscreen] = useState(false);
  const [zoomScale, setZoomScale] = useState(ZOOM_MIN);
  const zoomLabelRef = useRef<HTMLSpanElement | null>(null);

  const hasPublicDownloadVariant = Boolean(urlPublic && urlPublic !== '#');
  const hasWithAgentDownload = Boolean(url && url !== '#');

  // Download toggle only — preview always uses the with-agent URL.
  const [downloadWithAgent, setDownloadWithAgent] = useState(hasWithAgentDownload);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) {
      setMobileFullscreen(false);
      setZoomScale(ZOOM_MIN);
      return;
    }
    const update = () => {
      const viewportCap = isMobile
        ? mobileFullscreen
          ? window.innerWidth - 32
          : window.innerWidth * 0.75 - 48
        : Math.min(window.innerWidth - 48, 720);
      setPageWidth(Math.max(280, viewportCap));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [open, isMobile, mobileFullscreen]);

  // Reset the download toggle when the dialog opens — default to
  // with-agent when that variant exists, otherwise public-only.
  useEffect(() => {
    if (open) setDownloadWithAgent(hasWithAgentDownload);
  }, [open, hasWithAgentDownload]);

  const previewSourceUrl = hasWithAgentDownload ? (url as string) : null;
  const downloadUrl = useMemo(() => {
    if (downloadWithAgent) {
      return hasWithAgentDownload ? (url as string) : null;
    }
    return hasPublicDownloadVariant ? (urlPublic as string) : null;
  }, [downloadWithAgent, url, urlPublic, hasWithAgentDownload, hasPublicDownloadVariant]);

  // Preview source: direct portal temp URL (with-agent EDM) or a blob
  // from the download proxy (other files).
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewSrcIsBlob, setPreviewSrcIsBlob] = useState(false);
  const [kind, setKind] = useState<PreviewKind>('pdf');
  const [previewState, setPreviewState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPreviewSrc(null);
      setPreviewSrcIsBlob(false);
      setPreviewState('idle');
      setPreviewError(null);
      return;
    }

    if (!previewSourceUrl) {
      setPreviewSrc(null);
      setPreviewSrcIsBlob(false);
      setPreviewState('error');
      setPreviewError(
        hasPublicDownloadVariant
          ? 'Preview is only available for the version with your agent details.'
          : 'Preview unavailable.',
      );
      return;
    }

    const resolvedKind = resolvePreviewKind('', previewSourceUrl, urlPublic, filename);
    if (touchPreview && resolvedKind === 'video') {
      setPreviewSrc(null);
      setPreviewSrcIsBlob(false);
      setKind(resolvedKind);
      setPreviewState('error');
      setPreviewError(
        'Video preview is not available on mobile and tablet devices. Use Download to save the file.',
      );
      return;
    }

    let cancelled = false;

    const setPreviewErrorMessage = (message: string) => {
      setPreviewSrc(null);
      setPreviewSrcIsBlob(false);
      setPreviewState('error');
      if (isMobile) {
        setPreviewError(
          message || 'Could not load preview on this device. Use Download to open the file.',
        );
      } else {
        setPreviewError(message || 'Could not load preview.');
      }
    };

    const loadPreview = async () => {
      setPreviewState('loading');
      setPreviewError(null);
      setPreviewSrc(null);
      setPreviewSrcIsBlob(false);

      const useGetEdmTempUrl = isDownloadEdmUrl(previewSourceUrl);

      try {
        if (useGetEdmTempUrl) {
          const edmId = parseEdmIdFromDownloadEdmUrl(previewSourceUrl);
          if (!edmId) {
            throw new Error('Could not resolve EDM preview URL');
          }
          const directUrl = await resolveEdmWithAgentPreviewUrl(edmId);
          if (cancelled) return;
          const directKind =
            kindFromUrl(directUrl) !== 'unsupported'
              ? kindFromUrl(directUrl)
              : resolvedKind;
          setPreviewSrc(directUrl);
          setPreviewSrcIsBlob(false);
          setKind(directKind);
          setPreviewState('ready');
          return;
        }

        const isPortalHostUrl = (rawUrl: string | null | undefined): boolean => {
          if (!rawUrl) return false;
          try {
            return new URL(rawUrl).hostname === 'portal.huttonsgroup.com';
          } catch {
            return false;
          }
        };

        // External (non-portal) assets — e.g. Singmap CDN gallery/video URLs —
        // load directly in the browser. The preview proxy only whitelists
        // portal.huttonsgroup.com, and VPC/proxy is unnecessary for public CDNs.
        if (!isPortalHostUrl(previewSourceUrl)) {
          const directKind =
            resolvedKind !== 'unsupported'
              ? resolvedKind
              : kindFromUrl(previewSourceUrl);
          if (directKind === 'unsupported') {
            throw new Error('Preview is not available for this file type.');
          }
          setPreviewSrc(previewSourceUrl);
          setPreviewSrcIsBlob(false);
          setKind(directKind);
          setPreviewState('ready');
          return;
        }

        const { objectUrl, contentType } = await fetchPreviewBlob(previewSourceUrl);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewSrc(objectUrl);
        setPreviewSrcIsBlob(true);
        setKind(resolvePreviewKind(contentType, previewSourceUrl, urlPublic, filename));
        setPreviewState('ready');
      } catch (err) {
        if (cancelled) return;
        const message = (err as Error)?.message || '';
        setPreviewErrorMessage(message);
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
    };
  }, [open, previewSourceUrl, urlPublic, filename, isMobile, touchPreview, hasPublicDownloadVariant]);

  useEffect(
    () => () => {
      if (previewSrcIsBlob && previewSrc) {
        URL.revokeObjectURL(previewSrc);
      }
    },
    [previewSrc, previewSrcIsBlob],
  );

  useEffect(() => {
    setZoomScale(ZOOM_MIN);
  }, [previewSrc]);

  const downloadFilename = useMemo(() => {
    // Prefer the ebook/display name (title or filename prop) over CDN path
    // segments like `Document.PDF` so the saved file matches what the user sees.
    const preferred = (filename || title || '').trim();
    if (preferred) {
      const urlExt = extensionFromUrlOrName(downloadUrl, preferred);
      return buildSafeDownloadFilename(
        preferred,
        urlExt ? `x.${urlExt}` : filename,
      );
    }
    if (!downloadUrl) return 'download.pdf';
    return deriveFilename(downloadUrl, 'download.pdf');
  }, [downloadUrl, filename, title]);

  const handleDownload = async () => {
    if (!downloadUrl || downloading) return;
    if (onDownloadClick) onDownloadClick();
    setDownloading(true);
    try {
      if (forceBlobDownload) {
        await triggerForceDownload(downloadUrl, downloadFilename);
      } else {
        triggerDownload(downloadUrl, downloadFilename);
      }
    } finally {
      setDownloading(false);
    }
  };

  const showMobilePreviewChrome =
    isMobile &&
    previewState === 'ready' &&
    Boolean(previewSrc) &&
    (kind === 'pdf' || kind === 'image');

  const exitMobileFullscreen = () => {
    setMobileFullscreen(false);
    setZoomScale(ZOOM_MIN);
  };

  const handleClose = () => {
    exitMobileFullscreen();
    onClose();
  };

  const handleZoomScaleChange = (scale: number) => {
    setZoomScale(roundZoom(scale));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile && mobileFullscreen}
      maxWidth={isMobile ? false : 'md'}
      fullWidth={!isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text)',
          borderRadius: isMobile && mobileFullscreen ? 0 : 2,
          ...(isMobile && !mobileFullscreen
            ? {
                display: 'flex',
                flexDirection: 'column',
                width: MOBILE_DIALOG_WIDTH,
                maxWidth: MOBILE_DIALOG_WIDTH,
                height: MOBILE_DIALOG_HEIGHT,
                maxHeight: MOBILE_DIALOG_HEIGHT,
                m: 'auto',
              }
            : {}),
          ...(isMobile && mobileFullscreen
            ? {
                display: 'flex',
                flexDirection: 'column',
                m: 0,
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                maxHeight: '100%',
              }
            : {}),
        },
      }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          fontWeight: 700,
          color: 'var(--color-text-strong)',
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          variant="inherit"
          noWrap
          title={title}
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
      </DialogTitle>
      <IconButton
        onClick={handleClose}
        aria-label="Close preview"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'var(--color-text-muted)',
          zIndex: 2,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: 'var(--color-surface-elevated)',
          minHeight: isMobile ? 0 : { sm: 480 },
          flex: isMobile ? 1 : undefined,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {previewState === 'loading' && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{ flex: 1, py: 6 }}
          >
            <CircularProgress size={32} />
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              Loading preview…
            </Typography>
          </Stack>
        )}

        {previewState === 'ready' && previewSrc && kind === 'pdf' && (
          touchPreview ? (
            <TouchPdfScrollPreview
              pdfUrl={previewSrc}
              pageWidth={pageWidth}
              zoomScale={mobileFullscreen ? zoomScale : ZOOM_MIN}
              pinchZoom={mobileFullscreen}
              onZoomScaleChange={handleZoomScaleChange}
              zoomLabelRef={zoomLabelRef}
            />
          ) : (
            <Box
              component="iframe"
              key={previewSrc}
              src={previewSrc}
              title={title}
              sx={{
                width: '100%',
                height: { sm: 520, md: 640 },
                border: 0,
                bgcolor: '#fff',
              }}
            />
          )
        )}

        {previewState === 'ready' && previewSrc && kind === 'image' && (
          <PinchZoomSurface
            enabled={mobileFullscreen}
            zoomScale={mobileFullscreen ? zoomScale : ZOOM_MIN}
            onZoomScaleChange={handleZoomScaleChange}
            zoomLabelRef={zoomLabelRef}
            sx={{
              width: '100%',
              flex: isMobile ? '1 1 0' : undefined,
              minHeight: isMobile ? 0 : undefined,
              overflow: 'auto',
              p: { xs: 1, sm: 2 },
              bgcolor: '#fff',
            }}
          >
            <Box
              component="img"
              key={previewSrc}
              src={previewSrc}
              alt={title}
              sx={{
                maxWidth: '100%',
                maxHeight: isMobile ? '100%' : 'calc(85vh - 220px)',
                minWidth: 0,
                minHeight: 0,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </PinchZoomSurface>
        )}

        {previewState === 'ready' && previewSrc && kind === 'video' && (
          <Box
            sx={{
              width: '100%',
              flex: isMobile ? '1 1 0' : undefined,
              minHeight: isMobile ? 0 : undefined,
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 1, sm: 2 },
              bgcolor: '#000',
            }}
          >
            <Box
              component="video"
              key={previewSrc}
              src={previewSrc}
              controls
              autoPlay
              playsInline
              sx={{
                width: '100%',
                maxWidth: '100%',
                maxHeight: isMobile ? '100%' : { sm: 520, md: 640 },
                display: 'block',
              }}
            />
          </Box>
        )}

        {previewState === 'ready' && kind === 'unsupported' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              Preview not supported for this file type. Use the Download
              button below to save the file.
            </Typography>
          </Box>
        )}

        {previewState === 'error' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              {previewError || 'Preview unavailable.'}
            </Typography>
          </Box>
        )}

        {showMobilePreviewChrome && (
          <Stack
            spacing={1}
            sx={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              zIndex: 3,
              alignItems: 'flex-end',
            }}
          >
            {mobileFullscreen && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  bgcolor: 'color-mix(in srgb, var(--color-surface) 92%, transparent)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 999,
                  px: 0.75,
                  py: 0.25,
                  boxShadow: 'var(--color-shadow-card)',
                }}
              >
                <Typography
                  ref={zoomLabelRef}
                  component="span"
                  variant="caption"
                  sx={{
                    minWidth: 36,
                    textAlign: 'center',
                    fontWeight: 700,
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {Math.round(zoomScale * 100)}%
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Reset zoom"
                  onClick={() => setZoomScale(ZOOM_MIN)}
                  disabled={zoomScale === ZOOM_MIN}
                  sx={{ color: 'var(--color-text-strong)' }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            <IconButton
              aria-label={mobileFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
              onClick={() => {
                if (mobileFullscreen) {
                  exitMobileFullscreen();
                } else {
                  setMobileFullscreen(true);
                }
              }}
              sx={{
                bgcolor: 'color-mix(in srgb, var(--color-surface) 92%, transparent)',
                border: '1px solid var(--color-border)',
                boxShadow: 'var(--color-shadow-card)',
                color: 'var(--color-text-strong)',
                '&:hover': {
                  bgcolor: 'var(--color-surface)',
                },
              }}
            >
              {mobileFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Stack>
        )}
      </DialogContent>

      {/* Footer — preview note + download toggle (when applicable). */}
      <Stack
        spacing={1.25}
        sx={{
          p: { xs: 1.5, sm: 2 },
          bgcolor: 'var(--color-surface)',
          flexShrink: 0,
        }}
      >
        {hasPublicDownloadVariant && (
          <Typography
            variant="caption"
            sx={{ color: 'var(--color-text-muted)', lineHeight: 1.45 }}
          >
            Preview shows your personalised version with agent details.
            Choose which variant to download below.
          </Typography>
        )}
        {hasPublicDownloadVariant && (
          <FormControlLabel
            control={
              <Checkbox
                checked={downloadWithAgent}
                onChange={(e) => setDownloadWithAgent(e.target.checked)}
                sx={{
                  color: 'var(--color-accent)',
                  '&.Mui-checked': { color: 'var(--color-accent)' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Download with agent details
              </Typography>
            }
            sx={{ m: 0 }}
          />
        )}
        <Button
          onClick={handleDownload}
          startIcon={
            downloading ? (
              <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
            ) : (
              <DownloadIcon />
            )
          }
          variant="contained"
          disableElevation
          fullWidth
          disabled={!downloadUrl || downloading}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            py: 1,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            '&:hover': { bgcolor: 'var(--color-accent-strong)' },
          }}
        >
          {downloading ? 'Preparing…' : 'Download'}
        </Button>
      </Stack>
    </Dialog>
  );
}

export default FilePreviewDialog;
